import type { OutputData } from '@editorjs/editorjs';
import type { DeepPartial } from 'react-hook-form';

import type { IncompleteEventFormComplete } from '../../../forms/event/IncompleteEventForm/schema.ts';
import { getBlockText } from '../../../lib/getBlockText.ts';

interface VenueLite {
  commercial_name?: string | null;
  city?: string | null;
}

interface OptionLookup {
  category?: { value: string; label: string }[];
  targetAudiences?: { value: string; label: string }[];
  eventDetails?: { value: string; label: string }[];
}

/**
 * Builds the postMessage payload sent to the /event-preview iframe on the
 * customer-facing frontend. Keeps a normalized, plain-data view-model:
 * labels are resolved here on the parent side, descriptions are flattened
 * to plain-text paragraphs (no HTML strings cross the iframe boundary).
 */
export const buildPreviewMessage = (
  form: DeepPartial<IncompleteEventFormComplete> | undefined,
  venue: VenueLite | undefined,
  options: OptionLookup,
) => {
  if (!form) return null;

  const description = form.description as OutputData | undefined;
  const descriptionParagraphs =
    description?.blocks
      ?.filter((block) => block?.type === 'paragraph')
      .map((block) => stripHtml(getBlockText(block) ?? ''))
      .filter((text): text is string => !!text && text.length > 0) ?? [];

  const categoryLabel = options.category?.find((c) => c.value === form.category)?.label ?? null;

  const tagIds = new Set((form.tags ?? []).filter((t): t is string => !!t));
  const targetAudiences = options.targetAudiences?.filter((opt) => tagIds.has(opt.value)).map((opt) => opt.label) ?? [];

  const detailIds = new Set((form.eventDetails ?? []).filter((d): d is string => !!d));
  const eventDetails = options.eventDetails?.filter((opt) => detailIds.has(opt.value)).map((opt) => opt.label) ?? [];

  return {
    type: 'preview-data' as const,
    data: {
      name: form.name ?? '',
      descriptionParagraphs,
      image: typeof form.image === 'string' ? form.image : null,
      category: categoryLabel,
      targetAudiences,
      eventDetails,
      start: form.start instanceof Date ? form.start.toISOString() : null,
      end: form.end instanceof Date ? form.end.toISOString() : null,
      type: form.eventType ?? null,
      venue: venue?.commercial_name ? { name: venue.commercial_name, city: venue.city ?? null } : null,
      customVenue: form.customVenue ?? null,
      onlineLink: form.onlineLink ?? null,
      websiteLink: form.websiteLink ?? null,
      instagramLink: form.instagramLink ?? null,
      tiktokLink: form.tiktokLink ?? null,
      spotifyLink: form.spotifyLink ?? null,
      ticketshopLink: form.ticketshopLink ?? null,
    },
  };
};

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() ?? '';
};
